/**
 * Open enums (D37, docs/compatibility.md §4.3): an enum a newer writer may
 * extend and an older reader receives.
 *
 * A closed `z.enum` refuses a value it does not know, and tolerant parsing
 * (`parsing.ts`) forgives unknown KEYS only — so one new value (the
 * `role: 'witness'` of bbc09b3 in `GET /status`) failed every older SPA's read
 * as "contract drift". An open enum reads any unknown string as `'unknown'`,
 * which every reader must handle: the SPA and the CLI render it as a neutral
 * "unknown (a newer release?)", never a crash.
 *
 * - The input must still be a string: a missing field or a number is real
 *   drift and still fails.
 * - `'unknown'` is a member of the declared values, so a writer may send it
 *   too (protobuf's `*_UNSPECIFIED`, AIP-126).
 * - It is marked `x-extensible-enum` in the generated OpenAPI, so the
 *   breaking-change gate (wave 2) accepts a new value there and nowhere else.
 *
 * Use it for values a reader RECEIVES from another version. A request-side
 * enum a server validates stays closed.
 */
import { z } from 'zod';

export const UNKNOWN_ENUM_VALUE = 'unknown';

type Values = readonly [string, ...string[]];

/** `z.enum([...values, 'unknown'])` that reads any other string as `'unknown'`. */
export function openEnum<const T extends Values>(values: T) {
  const known: [...T, typeof UNKNOWN_ENUM_VALUE] = [...values, UNKNOWN_ENUM_VALUE];
  return z
    .string()
    .pipe(z.enum(known).catch(UNKNOWN_ENUM_VALUE))
    .meta({ 'x-extensible-enum': known });
}

/** Is this an open enum's fallback? For a reader's `unknown` branch. */
export function isUnknownEnumValue(value: string): value is typeof UNKNOWN_ENUM_VALUE {
  return value === UNKNOWN_ENUM_VALUE;
}

/**
 * A `z.toJSONSchema` `override` that renders an open enum as the gate reads it: its values under
 * `x-extensible-enum` (oasdiff then accepts a new value there and nowhere else), and without the `default` zod
 * derives from `.catch` — the fallback is the reader's, not a default a writer may omit the field for.
 */
export function openEnumJsonSchema(ctx: { readonly zodSchema: z.core.$ZodType; readonly jsonSchema: z.core.JSONSchema.BaseSchema }): void {
  const meta = z.globalRegistry.get(ctx.zodSchema);
  const values = meta?.['x-extensible-enum'];
  if (values === undefined) return;
  delete ctx.jsonSchema.default;
  ctx.jsonSchema['x-extensible-enum'] = values;
}

/**
 * A write-back never replaces a known value with `'unknown'` (review finding, D37): a reader that does not know a
 * value holds `'unknown'`, and writing its whole record back (a claim change, a pair merge) must not destroy the real
 * value where it is still held. The known one stays.
 */
export function keepKnown<T extends string>(incoming: T, held: T): T {
  return incoming === UNKNOWN_ENUM_VALUE && held !== UNKNOWN_ENUM_VALUE ? held : incoming;
}
